import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../i18n/I18nProvider";
import { Check, Loader2, AlertCircle, CalendarX } from "lucide-react";
import LicenseUploadForm from "../components/LicenseUploadForm";

const MAX_ATTEMPTS = 6;
const INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", data: null, error: null });
  const polled = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState({ phase: "error", error: "Missing session id" }); return; }
    let stopped = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          localStorage.removeItem("pendingBookingId");
          setState({ phase: "paid", data });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", data });
          return;
        }
        polled.current += 1;
        if (polled.current >= MAX_ATTEMPTS) {
          setState({ phase: "timeout", data });
          return;
        }
        if (!stopped) setTimeout(poll, INTERVAL_MS);
      } catch (e) {
        setState({ phase: "error", error: e.response?.data?.detail || "Error" });
      }
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-8 pb-16 px-4" data-testid="payment-success-page">
      <div className="max-w-2xl w-full mx-auto">
        {state.phase === "polling" && (
          <div className="text-center py-24" data-testid="payment-polling">
            <Loader2 className="w-12 h-12 animate-spin text-scooter-yellow mx-auto mb-6" />
            <h1 className="font-heading font-black text-3xl tracking-tighter mb-2">Verifica pagamento...</h1>
            <p className="text-zinc-400 text-sm">Verifying payment · Zahlung wird geprüft</p>
          </div>
        )}
        {state.phase === "paid" && (
          <div className="border-2 border-green-500/60 bg-green-500/5 p-8 text-center animate-pulse-once" data-testid="payment-success">
            <div className="w-20 h-20 mx-auto bg-green-500 text-white flex items-center justify-center rounded-full mb-6 shadow-2xl shadow-green-500/50"><Check className="w-10 h-10" strokeWidth={3} /></div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-green-400 mb-2">Pagamento confermato</div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter text-center mb-3 text-white">Grazie, prenotazione confermata!</h1>
            <p className="text-zinc-400 text-center text-sm mb-6">La tua email è in arrivo. Ci vediamo a San Cassiano — ti aspettiamo per l&apos;avventura in scooter!</p>
            {state.data?.booking && (
              <div className="bg-black border border-white/10 p-5 text-sm space-y-2 mb-6 text-left">
                <div className="flex justify-between"><span className="text-zinc-500">ID prenotazione</span><span className="font-mono text-xs text-scooter-yellow font-bold">{state.data.booking.booking_id}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Periodo</span><span>{state.data.booking.start_date} → {state.data.booking.end_date}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Quantità</span><span>{state.data.booking.quantity}× NIU NQiX 500</span></div>
                <div className="flex justify-between text-lg pt-2 border-t border-white/10"><span className="text-zinc-400">Pagato</span><span className="text-green-400 font-bold">€{state.data.booking.total_price.toFixed(2)}</span></div>
              </div>
            )}
            <div
              className="border border-scooter-yellow/40 bg-scooter-yellow/5 p-4 mb-6 text-left flex gap-3 items-start"
              data-testid="cancel-policy-info"
            >
              <CalendarX className="w-5 h-5 text-scooter-yellow flex-shrink-0 mt-0.5" strokeWidth={2.2} />
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-scooter-yellow mb-1" data-testid="cancel-policy-title">
                  {t.payment.cancel_title}
                </div>
                <div className="text-xs text-zinc-300 leading-relaxed" data-testid="cancel-policy-desc">
                  {t.payment.cancel_desc}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => navigate("/dashboard")} className="flex-1 h-12 bg-scooter-yellow text-black font-bold text-xs tracking-[0.2em] uppercase hover:bg-scooter-yellow-hover" data-testid="go-dashboard-btn">{t.nav.dashboard}</button>
              <button onClick={() => navigate("/")} className="flex-1 h-12 border border-white/20 font-bold text-xs tracking-[0.2em] uppercase hover:bg-white/5" data-testid="go-home-btn">Home</button>
            </div>
          </div>
        )}
        {(state.phase === "timeout" || state.phase === "expired" || state.phase === "error") && (
          <div className="border border-red-500/40 bg-red-500/5 p-10 text-center max-w-lg mx-auto" data-testid="payment-issue">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h1 className="font-heading font-black text-3xl tracking-tighter mb-3">
              {state.phase === "expired" ? "Sessione scaduta" : state.phase === "error" ? "Errore" : "In elaborazione"}
            </h1>
            <p className="text-zinc-400 text-sm mb-6">{state.error || "Controlla la tua email a breve per la conferma."}</p>
            <button onClick={() => navigate("/")} className="h-12 px-8 bg-scooter-yellow text-black font-bold text-xs tracking-[0.2em] uppercase" data-testid="retry-home-btn">Home</button>
          </div>
        )}
      </div>
    </div>
  );
}
